import React, { useState, useEffect } from 'react';
import { CheckCircle, XCircle, Users, Clock } from 'lucide-react';
import { useApp } from '../context/AppContext';
import { sessionService } from '../services/sessionService';

interface PendingApproval {
  id: string;
  sessionId: string;
  userId: string;
  requestedAt: any;
  status: string;
}

const ApprovalPanel: React.FC = () => {
  const { state } = useApp();
  const [pendingApprovals, setPendingApprovals] = useState<PendingApproval[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!state.currentSession || !state.currentSession.settings.requireApproval) {
      return;
    }

    // Only show approvals to session creator
    if (state.currentSession.createdBy !== state.currentUser.id) {
      return;
    }

    const unsubscribe = sessionService.listenForPendingApprovals(
      state.currentSession.id,
      (approvals) => {
        setPendingApprovals(approvals);
      }
    );

    return () => unsubscribe();
  }, [state.currentSession, state.currentUser.id]);

  const handleApprove = async (userId: string) => {
    if (!state.currentSession) return;
    
    setIsLoading(true);
    try {
      await sessionService.approveUserJoin(
        state.currentSession.id,
        userId,
        state.currentUser.id
      );
      console.log(`✅ Approved user ${userId} to join session`);
    } catch (error) {
      console.error('Failed to approve user:', error);
      alert('Failed to approve user. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleReject = async (userId: string) => {
    if (!state.currentSession) return;
    
    setIsLoading(true);
    try {
      await sessionService.rejectUserJoin(
        state.currentSession.id,
        userId,
        state.currentUser.id
      );
      console.log(`❌ Rejected user ${userId} join request`);
    } catch (error) {
      console.error('Failed to reject user:', error);
      alert('Failed to reject user. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const formatTime = (timestamp: any) => {
    try {
      const date = timestamp?.toDate ? timestamp.toDate() : new Date(timestamp);
      return date.toLocaleTimeString();
    } catch (error) {
      return 'Unknown';
    }
  };

  // Don't show panel if no approvals or not the creator
  if (!state.currentSession || 
      !state.currentSession.settings.requireApproval ||
      state.currentSession.createdBy !== state.currentUser.id ||
      pendingApprovals.length === 0) {
    return null;
  }

  return (
    <div className="bg-dark-700 rounded-lg p-4 border border-dark-600">
      <div className="flex items-center space-x-2 mb-3">
        <Users className="h-5 w-5 text-accent-500" />
        <h3 className="text-white font-medium">Pending Approvals</h3>
        <span className="bg-accent-600 text-white text-xs px-2 py-1 rounded-full">
          {pendingApprovals.length}
        </span>
      </div>

      <div className="space-y-3">
        {pendingApprovals.map((approval) => (
          <div
            key={approval.id}
            className="bg-dark-600 rounded-lg p-3 border border-dark-500"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="w-8 h-8 bg-accent-500 rounded-full flex items-center justify-center">
                  <span className="text-white text-sm font-bold">
                    {approval.userId.slice(-2).toUpperCase()}
                  </span>
                </div>
                <div>
                  <p className="text-white text-sm font-medium">
                    User {approval.userId.slice(-6)}
                  </p>
                  <div className="flex items-center space-x-1 text-xs text-dark-400">
                    <Clock className="h-3 w-3" />
                    <span>Requested at {formatTime(approval.requestedAt)}</span>
                  </div>
                </div>
              </div>

              <div className="flex items-center space-x-2">
                <button
                  onClick={() => handleApprove(approval.userId)}
                  disabled={isLoading}
                  className="p-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                  title="Approve user"
                >
                  <CheckCircle className="h-4 w-4" />
                </button>
                <button
                  onClick={() => handleReject(approval.userId)}
                  disabled={isLoading}
                  className="p-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                  title="Reject user"
                >
                  <XCircle className="h-4 w-4" />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default ApprovalPanel;
